import {
  Col,
  Heading,
  Pill,
  Row,
  StatusInfo,
  Tabs,
} from "@george-labs.com/design-system"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useAppDispatch,
  useCaseEvents,
  useLoanCases,
  useStatusFilter,
} from "../../app/hooks"
import {
  setStatusFilter,
  StatusFilterType,
} from "../../app/slices/dashboardFiltersSlice"
import { ClosedCaseTypeList } from "../../utils/constants"
import { CaseItem } from "./CaseItem"
import "./Cases.css"
import { ProductCategoryTypeCodes } from "../../functions/caseHelper"
import { getPalAnnualRevisionKoNotifications } from "../../functions/caseEventsHelper"
import { LoanCaseDTO } from "@smartx/mikro-vt-client-api"

const statusFilterTypes: StatusFilterType[] = [
  "all",
  "actionRequired",
  "existing",
  "expired",
]

const Cases = () => {
  const { t } = useTranslation()
  const cases = useLoanCases()
  const statusFilter = useStatusFilter()
  const dispatch = useAppDispatch()
  const caseEvents = useCaseEvents()
  const [selectedTab, setSelectedTab] = useState(0)
  
  const setFilter = (filterType: StatusFilterType) => {
    dispatch(setStatusFilter(filterType))
  }

  useEffect(() => {
    dispatch(setStatusFilter("all"))
  }, [])

  const isActionRequired = useCallback(
    (item: LoanCaseDTO) => {
      const isRectification =
        !!item.loanCaseStatus.onlineInfo?.onlineRectification

      const events = caseEvents?.filter((event) => event.caseId === item.caseId)

      const hasKoNotifications = getPalAnnualRevisionKoNotifications(
        item.loanCaseStatus.status,
        item.caseId,
        events,
      )

      return (
        isRectification ||
        (ProductCategoryTypeCodes["klikk"]?.includes(item.productCode) &&
          hasKoNotifications)
      )
    },
    [caseEvents],
  )

  const isExpired = (item: LoanCaseDTO) =>
    ClosedCaseTypeList.includes(item.loanCaseStatus.status)

  const isExisting = (item: LoanCaseDTO) =>
    !isActionRequired(item) && !isExpired(item)

  const filteredCases = useMemo(() => {
    if (!cases) return []

    if (statusFilter === "all") {
      return [
        ...cases.filter(isActionRequired),
        ...cases.filter(isExisting),
        ...cases.filter(isExpired),
      ]
    }

    if (statusFilter === "actionRequired") {
      return cases.filter(isActionRequired)
    }

    if (statusFilter === "existing") {
      return cases.filter(isExisting)
    }

    return cases.filter(isExpired)
  }, [cases, statusFilter])

  const actionRequiredCounter = useMemo(
    () => cases?.filter(isActionRequired).length ?? 0,
    [cases, isActionRequired],
  )

  return (
    <>
      <Heading level={1}>{t("cases.headerTitle")}</Heading>

      <Tabs
        items={[
          { label: t("dashboard.filter.all") },
          {
            label: t("dashboard.filter.actionRequired"),
            pill: actionRequiredCounter ? (
              <Pill
                variant={Pill.VARIANT.UNREAD}
                value={actionRequiredCounter}
              />
            ) : null,
          },
          { label: t("dashboard.filter.existing") },
          { label: t("dashboard.filter.expired") },
        ]}
        selectedTab={selectedTab}
        onSelect={({ selectedTabIndex }) => {
          setSelectedTab(selectedTabIndex)
          setFilter(statusFilterTypes[selectedTabIndex])
        }}
      />

      <Row className="mb-5">
        {!filteredCases?.length && (
          <Col>
            <StatusInfo
              icon={StatusInfo.ICON.EMPTY_STATE_GENERIC}
              title={t("cases.noCaseText")}
            />
          </Col>
        )}
        {filteredCases?.map((item) => (
          <Col lg={4} key={item.caseId}>
            <CaseItem item={item} />
          </Col>
        ))}
      </Row>
    </>
  )
}

export default Cases
